import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductCode, ProductMetric } from 'chery-contracts';
import { useMemo, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Badge } from '../../components/ui/Badge';
import { Notice } from '../../components/ui/Notice';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TableCell, TableHead, TableHeaderCell, TableRow } from '../../components/ui/Table';
import { cn } from '../../lib/cn';
import { serverMessage } from '../../lib/form-helpers';
import { PILLAR_META } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { compatibleProductKpis } from './product-display';

/**
 * Setup → Product bindings (Phase 8; ADMIN/PM via `RequireRole`): every product × metric (sold, delivered) with a KPI
 * select limited to Number KPIs. Unbound rows are highlighted; choosing "Unbound" unbinds. The service re-checks every
 * binding (400 naming expected vs actual), so the select's filter is a convenience — the same rules as Board bindings.
 */
export const ProductBindingsPage = () => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const bindings = useQuery(orpc.board.productBindings.list.queryOptions({ input: {} }));
  const kpis = useQuery(orpc.kpis.list.queryOptions({ input: { includeInactive: true } }));
  const update = useMutation(orpc.board.productBindings.update.mutationOptions());
  const items = useMemo(() => bindings.data?.items ?? [], [bindings.data]);
  const unboundCount = items.filter((item) => item.kpi === null).length;

  const onChange = async (code: ProductCode, metric: ProductMetric, model: string, kpiId: string): Promise<void> => {
    setNotice(null);

    try {
      const saved = await update.mutateAsync({ code, metric, kpiId: kpiId === '' ? null : kpiId });
      const vars = { product: model, metric: t(`board.products.metrics.${metric}`) };

      setNotice({
        tone: 'success',
        text: saved.kpi ? t('board.productBindings.saved', { ...vars, name: saved.kpi.name }) : t('board.productBindings.unboundSaved', vars),
      });
      await queryClient.invalidateQueries({ queryKey: orpc.board.key() });
    } catch (error) {
      setNotice({ tone: 'danger', text: serverMessage(error) ?? t('errors.saveFailed') });
    }
  };

  if (bindings.isPending || kpis.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('app.loading')}
      </div>
    );
  }

  if (bindings.isError || kpis.isError) {
    return <Notice tone="danger">{t('errors.loadFailed')}</Notice>;
  }

  const options = compatibleProductKpis(kpis.data.items);

  return (
    <div
      className="space-y-4"
      data-product-bindings
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('pages.setupProductBindings')}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t('board.productBindings.subtitle')}</p>
        </div>
        <Badge
          tone={unboundCount > 0 ? 'danger' : 'muted'}
          className="text-xs"
        >
          {unboundCount > 0
            ? t('board.productBindings.unboundCount', { count: unboundCount, total: items.length })
            : t('board.productBindings.allBound')}
        </Badge>
      </div>

      {notice ? (
        <Notice
          tone={notice.tone}
          onDismiss={() => setNotice(null)}
        >
          <span data-binding-notice>{notice.text}</span>
        </Notice>
      ) : null}

      <Table>
        <TableHead>
          <tr>
            <TableHeaderCell>{t('board.productBindings.columns.product')}</TableHeaderCell>
            <TableHeaderCell>{t('board.productBindings.columns.metric')}</TableHeaderCell>
            <TableHeaderCell>{t('board.productBindings.columns.expected')}</TableHeaderCell>
            <TableHeaderCell>{t('board.productBindings.columns.aggregation')}</TableHeaderCell>
            <TableHeaderCell className="w-96">{t('board.productBindings.columns.kpi')}</TableHeaderCell>
          </tr>
        </TableHead>
        <tbody>
          {items.map((item) => {
            // Keep a bound KPI selectable even if it is not a Number KPI any more, so the select never lies.
            const current = item.kpi && !options.some((kpi) => kpi.id === item.kpi?.id) ? [item.kpi] : [];
            const slot = `${item.code}:${item.metric}`;

            return (
              <TableRow
                key={slot}
                className={cn(item.kpi === null && 'bg-destructive/5')}
                data-product-binding-row={slot}
                data-bound={item.kpi ? 'true' : 'false'}
              >
                <TableCell>
                  <p className="text-sm text-foreground">{item.model}</p>
                  <p className="font-mono text-xs text-muted-foreground">{item.code}</p>
                </TableCell>
                <TableCell className="text-xs">
                  <p className="text-foreground">{t(`board.products.metrics.${item.metric}`)}</p>
                  <p className="text-muted-foreground">{item.label}</p>
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">{t(`enums.unit.${item.expectedUnit}`)}</TableCell>
                <TableCell className="text-xs">{t(`board.sheets.aggregation.${item.aggregation}`)}</TableCell>
                <TableCell>
                  <Select
                    aria-label={`${item.code} ${item.metric} KPI`}
                    data-product-binding-select={slot}
                    value={item.kpi?.id ?? ''}
                    disabled={update.isPending}
                    hasError={item.kpi === null}
                    options={[
                      { value: '', label: options.length === 0 ? t('board.productBindings.noCompatible') : t('board.productBindings.none') },
                      ...[...current, ...options].map((kpi) => ({
                        value: kpi.id,
                        label: `${PILLAR_META[kpi.pillar].shortLabel} · ${kpi.name} (${kpi.code})${kpi.status === 'INACTIVE' ? ` — ${t('board.productBindings.inactive')}` : ''}`,
                      })),
                    ]}
                    onChange={(event) => void onChange(item.code, item.metric, item.model, event.target.value)}
                    className="h-8 text-xs"
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
